import { Injectable } from '@angular/core';
import { FailedTranslation } from '../upas/details/ai-translations/failed-translation.model';

@Injectable({
  providedIn: 'root',
})
export class ApiErrorService {
  private readonly defaultErrorMessage = `Something went wrong on our end. Please try again. If the problem continues, reach out to support.`;

  getMessages(error: any): string[] {
    if (this.hasNoApiErrorPayload(error)) {
      return [];
    }

    const payload = error?.error ?? error;
    const rawMessages =
      payload?.data?.error?.messages ??
      payload?.data?.messages ??
      payload?.error?.messages ??
      payload?.messages ??
      payload?.errors ??
      payload?.data?.error?.message ??
      payload?.data?.message ??
      payload?.error ??
      payload?.message;

    return this.normalizeMessages(rawMessages);
  }

  getMessage(error: any, fallbackMessage: string = this.defaultErrorMessage): string {
    return this.getMessages(error)[0] || fallbackMessage;
  }

  getTraceId(error: any): string {
    const payload = error?.error ?? error;

    return payload?.data?.error?.trace_id ??
      payload?.data?.trace_id ??
      payload?.error?.trace_id ??
      payload?.trace_id ??
      '';
  }

  getReferenceId(error: any): string {
    const payload = error?.error ?? error;

    return payload?.data?.error?.reference_id ??
      payload?.data?.reference_id ??
      payload?.error?.reference_id ??
      payload?.reference_id ??
      this.getTraceId(error);
  }

  // Reads AI translation failed_translations[] from the normalized BE error
  // (data.error.failed_translations) or the FE-synthesized pre-flight shape.
  getFailedTranslations(error: any): FailedTranslation[] {
    const payload = error?.error ?? error;

    return payload?.data?.error?.failed_translations ??
      payload?.failed_translations ??
      [];
  }

  private normalizeMessages(candidate: any): string[] {
    if (!candidate) {
      return [];
    }

    if (Array.isArray(candidate)) {
      return candidate.flatMap(message => this.normalizeMessages(message));
    }

    if (typeof candidate === 'object') {
      return Object.entries(candidate).flatMap(([field, messages]) =>
        this.normalizeMessages(messages).map(message => `${field} ${message}`)
      );
    }

    return [candidate.toString()];
  }

  private hasNoApiErrorPayload(error: any): boolean {
    return error?.status != null
      && Object.prototype.hasOwnProperty.call(error, 'error')
      && error.error == null;
  }
}
